/**
 * Catch'n'Merge wordmark, drawn in code (docs/CARTOON_ART.md).
 *
 * Built to read at HUD size: two words in calm rounded capitals (Fredoka 700, fattened a little
 * with a round stroke) on one shared baseline per word, evenly spaced, with only a hint of
 * tilt. Golden candy fill (pale lemon → deep gold) with a soft gloss band and a thin darker-gold
 * inner edge, one dark-caramel outline under every letter so each word reads as a single
 * sticker shape, and a thin hard drop. A small gold star badge with the "N" sits in the space
 * between the words and covers no letter.
 *
 * Variants:
 *   - "stacked": CATCH / ★N / MERGE (the left-column logo card), the badge tucked into the
 *                gap between the lines where the T|C and R|G seams leave room
 *   - "line":    CATCH ★N MERGE      (the vertical-layout header), the badge inline
 *
 * Palettes (`TOON_LOGO_PALETTES`) only change colours; the game uses `TOON_LOGO_PALETTE`. The
 * others stay for the gallery's side-by-side comparison (toon-art.html?section=logo).
 *
 * The art is fitted (contain) into the requested pixel box and baked once per
 * variant × palette × pixel size (`getToonLogoCanvas`). Layout is a pure function of glyph
 * metrics (`layoutToonLogo`) so it can be tested without a canvas.
 */

/** Font stack for the letters: the self-hosted Fredoka 700 first (see style.css @font-face). */
export var TOON_LOGO_FONT_FAMILY = 'Fredoka, "Baloo 2", system-ui, sans-serif';
export var TOON_LOGO_FONT_WEIGHT = 700;

/** Layout unit: glyphs are measured and laid out at this font size, then scaled to fit. */
var EM = 100;
var DEG = Math.PI / 180;

var GOLD = {
  top: "#fff8bd",
  hi: "#ffe55c",
  mid: "#ffcd2e",
  low: "#f9a91c",
  bottom: "#f08a12",
  edge: "#e08a12",
};

var SEA = {
  top: "#e2f7ff",
  hi: "#93e2fb",
  mid: "#4fcbf5",
  low: "#2fb2ec",
  bottom: "#2196d6",
  edge: "#1b82c2",
};

var STAR_GOLD = { rim: "#fff6d8", top: "#ffe86a", mid: "#ffcd2e", bottom: "#f7a81d" };
var STAR_CORAL = { rim: "#fff2e8", top: "#ffb0a2", mid: "#ff6b6b", bottom: "#e04f52" };

/**
 * Colour sets. `fills` is [CATCH, MERGE]. The outline must read on both the cream day card
 * (#fff6e2) and the navy night card (#24456d): it is a dark warm caramel or the HUD's navy ink,
 * never a mid-tone.
 */
export var TOON_LOGO_PALETTES = {
  // Dark caramel outline (the HUD's caramel label colour, much deeper), all gold.
  caramel: {
    outline: "#5a3214",
    outlineTop: "#6b3f1c",
    drop: "#371c08",
    fills: [GOLD, GOLD],
    star: STAR_GOLD,
    n: "#5a3214",
  },
  // The HUD's navy ink outline (cards, buttons, dial), all gold.
  ink: {
    outline: "#172a3a",
    outlineTop: "#233c52",
    drop: "#0b1620",
    fills: [GOLD, GOLD],
    star: STAR_GOLD,
    n: "#172a3a",
  },
  // Navy ink, CATCH gold and MERGE sea blue, a coral badge (the pre-canvas wordmark's colours).
  sea: {
    outline: "#172a3a",
    outlineTop: "#233c52",
    drop: "#0b1620",
    fills: [GOLD, SEA],
    star: STAR_CORAL,
    n: "#ffffff",
  },
};

/** The palette the game uses. */
export var TOON_LOGO_PALETTE = "caramel";

/**
 * Per-variant proportions, in EM units (font size 100).
 *   fat    letter fattening per side (round stroke)
 *   out    outline width outside the fattened letter
 *   sep    thin outline-coloured ring each letter carries, so neighbouring fills stay apart
 *   edge   darker inner edge of the fill
 *   drop   hard drop under the whole sticker
 *   track  letter spacing on top of the measured advance (+ 2 × fat)
 *   gloss  optional [top, middle, cut] white alphas of the gloss band (default soft fade)
 *
 * "line" and "stacked" are tuned to read at HUD size, where fat letters and a heavy outline
 * keep the word solid. "poster" is the one-line layout for large art (store graphics): less
 * fattening and a slimmer outline so the letter shapes (the A's counter) stay open, and a
 * gloss band with a crisp lower edge instead of one that fades out.
 */
var VARIANTS = {
  stacked: {
    words: ["CATCH", "MERGE"],
    fat: 3.5,
    out: 5.5,
    sep: 2,
    edge: 2.2,
    drop: 3.5,
    track: -1,
    gap: 30, // CATCH's fill bottom to MERGE's fill top
    // The badge sits in the gap on the seam after each word's middle letter, where CATCH's
    // T|C and MERGE's R|G leave a diamond-shaped pocket of background: its points reach into
    // that pocket and it covers no letter (2 EM clear in Fredoka 700).
    star: 0.32, // star outer radius as a share of the cap height
    starY: 0.43, // badge centre, as a share of the gap below CATCH's fill
    starRim: 2.5,
    starRot: -6,
  },
  line: {
    words: ["CATCH", "MERGE"],
    oneLine: true,
    fat: 3,
    out: 6.5,
    sep: 2,
    edge: 2.2,
    drop: 4.5,
    track: -2,
    star: 0.46, // star outer radius as a share of the cap height
    starGap: 3, // letter fill to badge rim, each side
    starRim: 3,
    starRot: -8,
  },
  poster: {
    words: ["CATCH", "MERGE"],
    oneLine: true,
    fat: 1.6,
    out: 4.6,
    sep: 1.6,
    edge: 2,
    drop: 4,
    track: -1,
    star: 0.46,
    starGap: 3,
    starRim: 2.6,
    starRot: -8,
    gloss: [0.85, 0.62, 0.5],
  },
};

/** Per-letter tilt in degrees (both words): a gentle outward lean, one shared baseline. */
var TILT = [-2, 1, 0, -1, 2];

var STAR_INNER = 0.6;

/**
 * @typedef {Object} GlyphMetrics
 * @property {number} width advance width
 * @property {number} left extent left of the (centre-aligned) pen position
 * @property {number} right extent right of the pen position
 * @property {number} ascent above the baseline
 * @property {number} descent below the baseline
 */

/**
 * @typedef {Object} LogoGlyph
 * @property {string} ch
 * @property {number} word 0 = CATCH, 1 = MERGE
 * @property {number} x pivot x (glyph centre)
 * @property {number} y pivot y (half the cap height above the baseline)
 * @property {number} rot radians
 * @property {number} base baseline y in the glyph's local (pivot) frame
 * @property {GlyphMetrics} m
 */

function rotPoint(px, py, rot, x, y) {
  var cs = Math.cos(rot), sn = Math.sin(rot);
  return [x + px * cs - py * sn, y + px * sn + py * cs];
}

function growBounds(b, pts) {
  for (var i = 0; i < pts.length; i++) {
    var p = pts[i];
    if (p[0] < b.x0) b.x0 = p[0];
    if (p[0] > b.x1) b.x1 = p[0];
    if (p[1] < b.y0) b.y0 = p[1];
    if (p[1] > b.y1) b.y1 = p[1];
  }
}

function glyphCorners(g, grow) {
  var m = g.m;
  var l = -m.left - grow, r = m.right + grow;
  var t = g.base - m.ascent - grow, btm = g.base + m.descent + grow;
  return [
    rotPoint(l, t, g.rot, g.x, g.y),
    rotPoint(r, t, g.rot, g.x, g.y),
    rotPoint(l, btm, g.rot, g.x, g.y),
    rotPoint(r, btm, g.rot, g.x, g.y),
  ];
}

/** Star vertices (radius `r`, rotated `rot`) around the origin. */
function starPoints(r, rot) {
  var pts = [];
  for (var i = 0; i < 10; i++) {
    var a = -Math.PI / 2 + (i * Math.PI) / 5;
    var d = i % 2 ? r * STAR_INNER : r;
    pts.push(rotPoint(Math.cos(a) * d, Math.sin(a) * d, rot, 0, 0));
  }
  return pts;
}

/** Extents of a unit star at `rot`: {x0, x1, y0, y1} relative to its centre. */
function unitStarBox(rot) {
  var b = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  growBounds(b, starPoints(1, rot));
  return b;
}

/** Lay one word out on a baseline, centred on `cx`; returns its glyphs. */
function layoutWord(word, wordIndex, cx, baseline, capH, cfg, measure) {
  var total = wordWidth(word, cfg, measure);
  var x = cx - total / 2;
  var out = [];
  for (var j = 0; j < word.length; j++) {
    var m = measure(word[j]);
    var adv = m.width + 2 * cfg.fat;
    if (j) x += cfg.track;
    out.push({
      ch: word[j],
      word: wordIndex,
      x: x + adv / 2,
      y: baseline - capH / 2,
      rot: (TILT[j] || 0) * DEG,
      base: capH / 2,
      m: m,
    });
    x += adv;
  }
  return out;
}

/** x of the seam between glyph `i` and the next one (middle of their spacing). */
function seamX(glyphs, i, cfg) {
  var a = glyphs[i], b = glyphs[i + 1];
  return (a.x + (a.m.width / 2 + cfg.fat) + b.x - (b.m.width / 2 + cfg.fat)) / 2;
}

function wordWidth(word, cfg, measure) {
  var total = 0;
  for (var i = 0; i < word.length; i++) total += measure(word[i]).width + 2 * cfg.fat + (i ? cfg.track : 0);
  return total;
}

/**
 * Lay the logo out in EM units (font size 100). Pure: glyph metrics come from `measure`.
 * @param {"stacked"|"line"} variant
 * @param {(ch: string) => GlyphMetrics} measure metrics of one capital at font size 100
 * @returns {{ cfg: Object, capH: number, glyphs: LogoGlyph[],
 *   star: {x:number,y:number,r:number,rot:number,box:{x0:number,y0:number,x1:number,y1:number}},
 *   bounds: {x0:number,y0:number,x1:number,y1:number} }}
 */
export function layoutToonLogo(variant, measure) {
  var cfg = VARIANTS[variant] || VARIANTS.stacked;
  var capH = measure("H").ascent;
  var rot = (cfg.starRot || 0) * DEG;
  var u = unitStarBox(rot);
  var glyphs;
  var star;
  if (cfg.oneLine) {
    var wCatch = wordWidth(cfg.words[0], cfg, measure);
    var wMerge = wordWidth(cfg.words[1], cfg, measure);
    var r = capH * cfg.star;
    var starW = r * (u.x1 - u.x0) + 2 * cfg.starRim;
    var slot = starW + 2 * cfg.starGap;
    var left = -(wCatch + slot + wMerge) / 2;
    glyphs = layoutWord(cfg.words[0], 0, left + wCatch / 2, 0, capH, cfg, measure)
      .concat(layoutWord(cfg.words[1], 1, left + wCatch + slot + wMerge / 2, 0, capH, cfg, measure));
    // Centre the badge's own box in the slot and on the cap height.
    star = {
      x: left + wCatch + slot / 2 - (r * (u.x0 + u.x1)) / 2,
      y: -capH / 2 - (r * (u.y0 + u.y1)) / 2,
      r: r,
      rot: rot,
    };
  } else {
    // MERGE's fill top sits `gap` below CATCH's fill bottom.
    var base2 = cfg.fat + cfg.gap + cfg.fat + capH;
    var top = layoutWord(cfg.words[0], 0, 0, 0, capH, cfg, measure);
    var bottom = layoutWord(cfg.words[1], 1, 0, base2, capH, cfg, measure);
    glyphs = top.concat(bottom);
    star = {
      x: (seamX(top, Math.floor(top.length / 2), cfg) + seamX(bottom, Math.floor(bottom.length / 2), cfg)) / 2,
      y: cfg.fat + cfg.gap * cfg.starY,
      r: capH * cfg.star,
      rot: rot,
    };
  }
  var rr = cfg.starRim;
  star.box = {
    x0: star.x + star.r * u.x0 - rr,
    x1: star.x + star.r * u.x1 + rr,
    y0: star.y + star.r * u.y0 - rr,
    y1: star.y + star.r * u.y1 + rr,
  };

  var b = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  var grow = cfg.fat + cfg.out;
  for (var i = 0; i < glyphs.length; i++) growBounds(b, glyphCorners(glyphs[i], grow));
  var sb = star.box, so = cfg.out;
  growBounds(b, [[sb.x0 - so, sb.y0 - so], [sb.x1 + so, sb.y1 + so]]);
  b.y1 += cfg.drop;

  return { cfg: cfg, capH: capH, glyphs: glyphs, star: star, bounds: b };
}

/**
 * Fit a layout's bounds (contain) into a w×h box; returns the scale and the EM-space origin offset.
 * @returns {{ k: number, ox: number, oy: number }} pixel = (em + o) * k
 */
export function fitToonLogo(bounds, w, h) {
  var bw = bounds.x1 - bounds.x0, bh = bounds.y1 - bounds.y0;
  if (!(bw > 0) || !(bh > 0) || !(w > 0) || !(h > 0)) return { k: 0, ox: 0, oy: 0 };
  var k = Math.min(w / bw, h / bh);
  return {
    k: k,
    ox: w / (2 * k) - (bounds.x0 + bw / 2),
    oy: h / (2 * k) - (bounds.y0 + bh / 2),
  };
}

// ---------------------------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------------------------

function fontAt(size) {
  return TOON_LOGO_FONT_WEIGHT + " " + size + "px " + TOON_LOGO_FONT_FAMILY;
}

function makeMeasure(c) {
  var memo = Object.create(null);
  return function (ch) {
    if (memo[ch]) return memo[ch];
    c.save();
    c.font = fontAt(EM);
    c.textAlign = "center";
    c.textBaseline = "alphabetic";
    var tm = c.measureText(ch);
    c.restore();
    var w = tm.width || EM * 0.6;
    var m = {
      width: w,
      left: tm.actualBoundingBoxLeft != null ? tm.actualBoundingBoxLeft : w / 2,
      right: tm.actualBoundingBoxRight != null ? tm.actualBoundingBoxRight : w / 2,
      ascent: tm.actualBoundingBoxAscent != null ? tm.actualBoundingBoxAscent : EM * 0.7,
      descent: tm.actualBoundingBoxDescent != null ? Math.max(0, tm.actualBoundingBoxDescent) : 0,
    };
    memo[ch] = m;
    return m;
  };
}

function textSetup(c, size) {
  c.font = fontAt(size || EM);
  c.textAlign = "center";
  c.textBaseline = "alphabetic";
  c.lineJoin = "round";
  c.lineCap = "round";
  c.miterLimit = 2;
}

/** The glyph shape grown by `o` EM units (fill + round stroke). */
function glyphShape(c, g, o, style) {
  c.fillStyle = style;
  c.fillText(g.ch, 0, g.base);
  if (o > 0) {
    c.strokeStyle = style;
    c.lineWidth = 2 * o;
    c.strokeText(g.ch, 0, g.base);
  }
}

function withGlyph(c, g, fn) {
  c.save();
  c.translate(g.x, g.y);
  c.rotate(g.rot);
  fn();
  c.restore();
}

function starPath(c, r) {
  var pts = starPoints(r, 0);
  c.beginPath();
  for (var i = 0; i < pts.length; i++) {
    if (i) c.lineTo(pts[i][0], pts[i][1]);
    else c.moveTo(pts[i][0], pts[i][1]);
  }
  c.closePath();
}

function withStar(c, star, fn) {
  c.save();
  c.translate(star.x, star.y);
  c.rotate(star.rot);
  fn();
  c.restore();
}

/** Every solid outline shape (letters + badge) in one colour: merges into one sticker contour. */
function paintOutlineShapes(c, L, color) {
  var cfg = L.cfg;
  for (var i = 0; i < L.glyphs.length; i++) {
    withGlyph(c, L.glyphs[i], function () {
      glyphShape(c, L.glyphs[i], cfg.fat + cfg.out, color);
    });
  }
  withStar(c, L.star, function () {
    starPath(c, L.star.r);
    c.lineJoin = "round";
    c.fillStyle = color;
    c.strokeStyle = color;
    c.lineWidth = 2 * (cfg.starRim + cfg.out);
    c.fill();
    c.stroke();
  });
}

function letterGradient(c, g, fill) {
  var top = g.base - g.m.ascent;
  var bottom = g.base + g.m.descent;
  var grd = c.createLinearGradient(0, top, 0, bottom);
  grd.addColorStop(0, fill.top);
  grd.addColorStop(0.3, fill.hi);
  grd.addColorStop(0.55, fill.mid);
  grd.addColorStop(0.8, fill.low);
  grd.addColorStop(1, fill.bottom);
  return grd;
}

/**
 * Soft gloss band: the letter shrunk away from its edges, kept only on its upper part, white
 * fading down to a soft cut. Built on a scratch layer, then laid over the letter.
 */
function paintGloss(c, scratch, L, g, xf) {
  var cfg = L.cfg;
  var s = scratch.getContext("2d");
  s.setTransform(1, 0, 0, 1, 0, 0);
  s.globalCompositeOperation = "source-over";
  s.clearRect(0, 0, scratch.width, scratch.height);
  s.setTransform(xf.a, xf.b, xf.c, xf.d, xf.e, xf.f);
  textSetup(s);
  withGlyph(s, g, function () {
    var inset = cfg.edge + 2.6 - cfg.fat; // erosion beyond the plain glyph
    s.fillStyle = "#fff";
    s.fillText(g.ch, 0, g.base);
    if (inset > 0) {
      s.globalCompositeOperation = "destination-out";
      s.strokeStyle = "#000";
      s.lineWidth = 2 * inset;
      s.strokeText(g.ch, 0, g.base);
    }
    var top = g.base - g.m.ascent - cfg.fat;
    var cut = top + (g.m.ascent + cfg.fat) * 0.42;
    var grd = s.createLinearGradient(0, top, 0, cut);
    var a = cfg.gloss || [0.8, 0.45, 0.18];
    grd.addColorStop(0, "rgba(255,255,255," + a[0] + ")");
    grd.addColorStop(0.7, "rgba(255,255,255," + a[1] + ")");
    grd.addColorStop(1, "rgba(255,255,255," + a[2] + ")");
    s.globalCompositeOperation = "source-in";
    s.fillStyle = grd;
    s.fillRect(-EM, top - 1, EM * 2, cut - top + 1);
    s.globalCompositeOperation = "destination-in";
    s.fillStyle = "#000";
    s.fillRect(-EM, top - EM, EM * 2, cut - top + EM);
    // A small white glint up and to the left, kept inside the band (letters whose upper-left
    // is empty, like A, simply show none).
    s.globalCompositeOperation = "source-atop";
    s.beginPath();
    s.ellipse(-g.m.left * 0.45, top + (cut - top) * 0.6, 6, 3.2, -0.6, 0, Math.PI * 2);
    s.fillStyle = "rgba(255,255,255,0.85)";
    s.fill();
  });
  s.setTransform(1, 0, 0, 1, 0, 0);
  s.globalCompositeOperation = "source-over";
  c.save();
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.drawImage(scratch, 0, 0);
  c.restore();
}

function paintLetter(c, scratch, L, g, xf, pal) {
  var cfg = L.cfg;
  var fill = pal.fills[g.word] || pal.fills[0];
  withGlyph(c, g, function () {
    glyphShape(c, g, cfg.fat + cfg.sep, pal.outline);
    glyphShape(c, g, cfg.fat, fill.edge);
    glyphShape(c, g, Math.max(0, cfg.fat - cfg.edge), letterGradient(c, g, fill));
  });
  if (scratch) paintGloss(c, scratch, L, g, xf);
}

function paintStar(c, L, pal, measure) {
  var cfg = L.cfg;
  var col = pal.star;
  var st = L.star;
  withStar(c, st, function () {
    var r = st.r;
    // Cream rim (the star grown by a round stroke), then the star body inside it.
    starPath(c, r);
    c.lineJoin = "round";
    c.fillStyle = col.rim;
    c.strokeStyle = col.rim;
    c.lineWidth = 2 * cfg.starRim;
    c.fill();
    c.stroke();
    starPath(c, r * 0.94);
    var grd = c.createLinearGradient(0, -r, 0, r * 0.8);
    grd.addColorStop(0, col.top);
    grd.addColorStop(0.55, col.mid);
    grd.addColorStop(1, col.bottom);
    c.fillStyle = grd;
    c.fill();
    // Soft gloss on the upper-left arms.
    c.save();
    starPath(c, r * 0.94);
    c.clip();
    c.beginPath();
    c.ellipse(-r * 0.32, -r * 0.5, r * 0.6, r * 0.34, -0.5, 0, Math.PI * 2);
    c.fillStyle = "rgba(255,255,255,0.45)";
    c.fill();
    c.restore();
    // The "N", upright and centred on the star's body.
    c.rotate(-st.rot);
    var nSize = r * 1.02;
    var capN = (measure("N").ascent / EM) * nSize;
    textSetup(c, nSize);
    c.fillStyle = pal.n;
    c.strokeStyle = pal.n;
    c.lineWidth = nSize * 0.05;
    c.fillText("N", 0, capN / 2 + r * 0.04);
    c.strokeText("N", 0, capN / 2 + r * 0.04);
  });
}

function createCanvas(w, h) {
  if (typeof document === "undefined") return null;
  try {
    var cv = document.createElement("canvas");
    cv.width = Math.max(1, w | 0);
    cv.height = Math.max(1, h | 0);
    return cv.getContext && cv.getContext("2d") ? cv : null;
  } catch (_e) {
    return null;
  }
}

function paletteFor(name) {
  return TOON_LOGO_PALETTES[name] || TOON_LOGO_PALETTES[TOON_LOGO_PALETTE];
}

/**
 * Draw the logo fitted (contain) into the pixel box (0, 0, w, h) of `c`, whose transform must
 * be the identity (the box is in canvas pixels, so the gloss scratch layer lines up).
 * @param {CanvasRenderingContext2D} c
 * @param {"stacked"|"line"} variant
 * @param {number} w
 * @param {number} h
 * @param {string} [palette] a TOON_LOGO_PALETTES key (default: TOON_LOGO_PALETTE)
 */
export function drawToonLogo(c, variant, w, h, palette) {
  var pal = paletteFor(palette);
  textSetup(c);
  var measure = makeMeasure(c);
  var L = layoutToonLogo(variant, measure);
  var fit = fitToonLogo(L.bounds, w, h);
  if (!(fit.k > 0)) return L;
  var cfg = L.cfg;
  c.save();
  c.scale(fit.k, fit.k);
  c.translate(fit.ox, fit.oy);
  textSetup(c);
  var xf = c.getTransform ? c.getTransform() : null;
  var scratch = xf ? createCanvas(c.canvas.width, c.canvas.height) : null;

  // 1. Hard drop, then the merged outline over it (lit a touch at the top).
  c.save();
  c.translate(0, cfg.drop);
  paintOutlineShapes(c, L, pal.drop);
  c.restore();
  var b = L.bounds;
  var og = c.createLinearGradient(0, b.y0, 0, b.y1);
  og.addColorStop(0, pal.outlineTop);
  og.addColorStop(0.45, pal.outline);
  og.addColorStop(1, pal.outline);
  paintOutlineShapes(c, L, og);

  // 2. Letters (each with its own thin ring, edge, fill and gloss), then the badge.
  for (var i = 0; i < L.glyphs.length; i++) paintLetter(c, scratch, L, L.glyphs[i], xf, pal);
  paintStar(c, L, pal, measure);
  c.restore();
  return L;
}

// ---------------------------------------------------------------------------------------------
// Bake cache + fonts
// ---------------------------------------------------------------------------------------------

var CACHE = new Map();
var CACHE_MAX = 8;

/** True once the Fredoka 700 face used by the logo has loaded (or no FontFaceSet exists). */
export function isToonLogoFontReady() {
  if (typeof document === "undefined" || !document.fonts) return true;
  var face = findLogoFace();
  if (face) return face.status === "loaded";
  try {
    return document.fonts.check(fontAt(EM));
  } catch (_e) {
    return true;
  }
}

function findLogoFace() {
  var hit = null;
  try {
    document.fonts.forEach(function (f) {
      if (!hit && /fredoka/i.test(String(f.family)) && String(f.weight) === String(TOON_LOGO_FONT_WEIGHT)) hit = f;
    });
  } catch (_e) { /* FontFaceSet without forEach */ }
  return hit;
}

/**
 * Resolves (true/false) once the logo font is usable; never rejects. Times out so a blocked
 * font request cannot keep the logo blank (the caller re-bakes if the font lands later).
 * @param {number} [timeoutMs]
 * @returns {Promise<boolean>}
 */
export function whenToonLogoFontReady(timeoutMs) {
  if (isToonLogoFontReady()) return Promise.resolve(true);
  var face = findLogoFace();
  var load;
  try {
    load = face ? face.load() : document.fonts.load(fontAt(EM));
  } catch (_e) {
    load = Promise.resolve();
  }
  var timeout = new Promise(function (resolve) {
    setTimeout(resolve, timeoutMs == null ? 2500 : timeoutMs);
  });
  return Promise.race([Promise.resolve(load).catch(function () {}), timeout]).then(isToonLogoFontReady);
}

/**
 * The logo baked into a canvas of `pxW`×`pxH` device pixels (memoised per variant, palette,
 * size and font state). Null without a 2D canvas.
 * @param {"stacked"|"line"} variant
 * @param {number} pxW
 * @param {number} pxH
 * @param {string} [palette] a TOON_LOGO_PALETTES key (default: TOON_LOGO_PALETTE)
 * @returns {HTMLCanvasElement|null}
 */
export function getToonLogoCanvas(variant, pxW, pxH, palette) {
  var w = Math.max(1, Math.round(pxW));
  var h = Math.max(1, Math.round(pxH));
  var pal = TOON_LOGO_PALETTES[palette] ? palette : TOON_LOGO_PALETTE;
  var key = variant + "|" + pal + "|" + w + "x" + h + "|" + (isToonLogoFontReady() ? "f" : "x");
  if (CACHE.has(key)) return CACHE.get(key);
  var cv = createCanvas(w, h);
  if (cv) {
    try {
      drawToonLogo(cv.getContext("2d"), variant, w, h, pal);
    } catch (err) {
      if (typeof console !== "undefined") console.error("[toon-logo] bake failed", err);
    }
  }
  CACHE.set(key, cv);
  if (CACHE.size > CACHE_MAX) CACHE.delete(CACHE.keys().next().value);
  return cv;
}

/** @internal */
export function __resetToonLogoCacheForTests() {
  CACHE.clear();
}
